import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import { Building2, ArrowLeft, Mail, Key, CheckCircle, AlertCircle } from 'lucide-react';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState<'email' | 'verify' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [foundUser, setFoundUser] = useState<any>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const generateVerificationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString(); // 6-digit code
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Find customer by email via public API endpoint
      const response = await fetch('https://sourcemygoods.com/api/public/lookup-customer-by-email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const result = await response.json();
      
      if (!result.success || !result.data) {
        setError(result.message || 'No user found with this email address');
        setIsLoading(false);
        return;
      }

      const customer = result.data;

      // Generate verification code
      const code = generateVerificationCode();
      setGeneratedCode(code);
      setFoundUser({ customerCode: customer.code, customerName: customer.name });

      // Send password reset email
      const emailResponse = await fetch('https://sourcemygoods.com/api/public/send-password-reset-email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: email,
          code: code,
          name: customer.name
        })
      });

      const emailResult = await emailResponse.json();

      if (emailResult.success) {
        toast({
          title: "Verification Code Sent!",
          description: `A 6-digit code has been sent to ${email}. Check your spam folder if you don't see it.`,
          duration: 8000,
        });
        setStep('verify');
      } else {
        throw new Error(emailResult.error || 'Failed to send verification email');
      }

    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
      console.error('Password reset error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (verificationCode !== generatedCode) {
      setError('Invalid verification code. Please try again.');
      return;
    }

    setStep('reset');
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    try {
      // Reset password via API
      const response = await fetch('https://sourcemygoods.com/api/public/reset-password.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_code: foundUser.customerCode,
          new_password: newPassword
        })
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Password Reset Successful",
          description: "Your password has been updated. You can now log in with your new password.",
        });

        navigate('/login');
      } else {
        throw new Error(result.error || 'Failed to reset password');
      }

    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
      console.error('Password reset error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Back to Login Link */}
        <div className="flex items-center text-sm">
          <Link to="/login" className="flex items-center text-blue-600 hover:text-blue-800">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Login
          </Link>
        </div>

        {/* Reset Password Card */}
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-blue-100 p-3 rounded-full">
                {step === 'email' && <Mail className="h-8 w-8 text-blue-600" />}
                {step === 'verify' && <Key className="h-8 w-8 text-blue-600" />}
                {step === 'reset' && <CheckCircle className="h-8 w-8 text-blue-600" />}
              </div>
            </div>
            <CardTitle className="text-2xl">
              {step === 'email' && 'Forgot Password?'}
              {step === 'verify' && 'Verify Your Email'}
              {step === 'reset' && 'Set New Password'}
            </CardTitle>
            <CardDescription>
              {step === 'email' && 'Enter your email address to reset your password'}
              {step === 'verify' && 'Enter the verification code sent to your email'}
              {step === 'reset' && 'Create a new password for your account'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Step 1: Email Entry */}
            {step === 'email' && (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send Verification Code'}
                </Button>
              </form>
            )}

            {/* Step 2: Verification Code */}
            {step === 'verify' && (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                {/* Spam Folder Reminder */}
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-800">
                    <strong>Check your spam/junk folder</strong> if you don't see the email in your inbox. The code expires in 10 minutes.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="code">Verification Code</Label>
                  <Input
                    id="code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="text-center text-2xl font-mono tracking-widest"
                    required
                  />
                  <p className="text-xs text-gray-500 text-center">
                    Code sent to {email}
                  </p>
                </div>

                <Button type="submit" className="w-full">
                  Verify Code
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={async () => {
                    const newCode = generateVerificationCode();
                    setGeneratedCode(newCode);
                    
                    try {
                      const emailResponse = await fetch('https://sourcemygoods.com/api/public/send-password-reset-email.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          to: email,
                          code: newCode,
                          name: foundUser?.customerName || 'there'
                        })
                      });

                      const result = await emailResponse.json();
                      
                      if (result.success) {
                        toast({
                          title: "New Code Sent",
                          description: `A new code has been sent to ${email}. Check spam folder if needed.`,
                          duration: 8000,
                        });
                      }
                    } catch (err) {
                      toast({
                        title: "Error",
                        description: "Failed to resend code. Please try again.",
                        variant: "destructive"
                      });
                    }
                  }}
                >
                  Resend Code
                </Button>
              </form>
            )}

            {/* Step 3: New Password */}
            {step === 'reset' && (
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
